import { hasExactly, intWithin, isPlainRecord, marketRuleSetting, resolveMarketRule } from './market-rule-kit.js';
// ---- rider.photo ------------------------------------------------------------------------------------------------------------
export const RIDER_PHOTO_KEY = 'rider.photo';
/** What happens to a photo taken on a phone whose face detector cannot run. */
export const RIDER_PHOTO_UNCHECKED_ACTIONS = ['accept_for_review', 'refuse'];
/** The epoch: the hold is already in force. */
export const RIDER_PHOTO_IN_FORCE = '1970-01-01T00:00:00.000Z';
/** The hold is not scheduled yet (a rider without a photo is reminded, never held): the last day the format can write. */
export const RIDER_PHOTO_NOT_SCHEDULED = '9999-12-31T00:00:00.000Z';
/** Whether an `enforceFrom` means "not scheduled yet" (any instant in the year 9999). */
export const riderPhotoHoldScheduled = (enforceFrom) => !enforceFrom.startsWith('9999-');
export const RIDER_PHOTO_BOUNDS = {
    graceRequests: { min: 0, max: 20 },
    retentionDays: { min: 0, max: 730 },
    minSidePx: { min: 120, max: 1_024 },
    minFaceWidthPercent: { min: 10, max: 90 },
    maxCenterOffsetPercent: { min: 5, max: 50 },
    minEyeOpenPercent: { min: 10, max: 95 },
};
export const DEFAULT_RIDER_PHOTO = {
    required: true,
    allowGallery: false,
    enforceFrom: RIDER_PHOTO_IN_FORCE,
    graceRequests: 0,
    retentionDays: 90,
    minSidePx: 240,
    faceCheck: { minFaceWidthPercent: 25, maxCenterOffsetPercent: 20, minEyeOpenPercent: 50, onDetectorUnavailable: 'accept_for_review' },
};
export const STRICT_RIDER_PHOTO = {
    required: true,
    allowGallery: false,
    enforceFrom: RIDER_PHOTO_IN_FORCE,
    graceRequests: 0,
    retentionDays: 0,
    minSidePx: 320,
    faceCheck: { minFaceWidthPercent: 30, maxCenterOffsetPercent: 15, minEyeOpenPercent: 60, onDetectorUnavailable: 'refuse' },
};
export const RIDER_PHOTO_POLICY_REASON_CODES = ['RIDER_PHOTO_GOVERNED', 'RIDER_PHOTO_DEFAULT', 'RIDER_PHOTO_INVALID'];
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
const instantValid = (v) => typeof v === 'string' && UTC_INSTANT.test(v) && Number.isFinite(Date.parse(v));
export function riderPhotoFaceCheckIssue(v) {
    if (!isPlainRecord(v))
        return 'INVALID_FACE_CHECK';
    if (!hasExactly(v, ['minFaceWidthPercent', 'maxCenterOffsetPercent', 'minEyeOpenPercent', 'onDetectorUnavailable']))
        return 'INVALID_FACE_CHECK';
    const b = RIDER_PHOTO_BOUNDS;
    if (!intWithin(v.minFaceWidthPercent, b.minFaceWidthPercent.min, b.minFaceWidthPercent.max))
        return 'INVALID_MIN_FACE_WIDTH_PERCENT';
    if (!intWithin(v.maxCenterOffsetPercent, b.maxCenterOffsetPercent.min, b.maxCenterOffsetPercent.max))
        return 'INVALID_MAX_CENTER_OFFSET_PERCENT';
    if (!intWithin(v.minEyeOpenPercent, b.minEyeOpenPercent.min, b.minEyeOpenPercent.max))
        return 'INVALID_MIN_EYE_OPEN_PERCENT';
    if (!RIDER_PHOTO_UNCHECKED_ACTIONS.includes(v.onDetectorUnavailable))
        return 'INVALID_ON_DETECTOR_UNAVAILABLE';
    return undefined;
}
export function riderPhotoIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    if (!hasExactly(v, ['required', 'allowGallery', 'enforceFrom', 'graceRequests', 'retentionDays', 'minSidePx', 'faceCheck']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.required !== 'boolean')
        return 'INVALID_REQUIRED';
    if (typeof v.allowGallery !== 'boolean')
        return 'INVALID_ALLOW_GALLERY';
    if (!instantValid(v.enforceFrom))
        return 'INVALID_ENFORCE_FROM';
    const b = RIDER_PHOTO_BOUNDS;
    if (!intWithin(v.graceRequests, b.graceRequests.min, b.graceRequests.max))
        return 'INVALID_GRACE_REQUESTS';
    if (!intWithin(v.retentionDays, b.retentionDays.min, b.retentionDays.max))
        return 'INVALID_RETENTION_DAYS';
    if (!intWithin(v.minSidePx, b.minSidePx.min, b.minSidePx.max))
        return 'INVALID_MIN_SIDE_PX';
    return riderPhotoFaceCheckIssue(v.faceCheck);
}
const copyFaceCheck = (f) => ({
    minFaceWidthPercent: f.minFaceWidthPercent,
    maxCenterOffsetPercent: f.maxCenterOffsetPercent,
    minEyeOpenPercent: f.minEyeOpenPercent,
    onDetectorUnavailable: f.onDetectorUnavailable,
});
export const RIDER_PHOTO_SPEC = {
    key: RIDER_PHOTO_KEY,
    issue: riderPhotoIssue,
    copy: (v) => ({
        required: v.required,
        allowGallery: v.allowGallery,
        enforceFrom: new Date(Date.parse(v.enforceFrom)).toISOString(),
        graceRequests: v.graceRequests,
        retentionDays: v.retentionDays,
        minSidePx: v.minSidePx,
        faceCheck: copyFaceCheck(v.faceCheck),
    }),
    defaultValue: DEFAULT_RIDER_PHOTO,
    strictValue: STRICT_RIDER_PHOTO,
    reasonPrefix: 'RIDER_PHOTO',
    constraintClass: 'privacy',
    // Never looser than a layer above: required if any layer requires it, the gallery only where every layer allows it, the earlier enforcement, the fewer grace
    // requests, the shorter history, the larger minimum side, the tighter face check, and refusing an unchecked photo wherever a layer refuses it.
    tighten: (d, e) => ({
        required: d.required || e.required,
        allowGallery: d.allowGallery && e.allowGallery,
        enforceFrom: Date.parse(d.enforceFrom) <= Date.parse(e.enforceFrom) ? new Date(Date.parse(d.enforceFrom)).toISOString() : new Date(Date.parse(e.enforceFrom)).toISOString(),
        graceRequests: Math.min(d.graceRequests, e.graceRequests),
        retentionDays: Math.min(d.retentionDays, e.retentionDays),
        minSidePx: Math.max(d.minSidePx, e.minSidePx),
        faceCheck: {
            minFaceWidthPercent: Math.max(d.faceCheck.minFaceWidthPercent, e.faceCheck.minFaceWidthPercent),
            maxCenterOffsetPercent: Math.min(d.faceCheck.maxCenterOffsetPercent, e.faceCheck.maxCenterOffsetPercent),
            minEyeOpenPercent: Math.max(d.faceCheck.minEyeOpenPercent, e.faceCheck.minEyeOpenPercent),
            onDetectorUnavailable: d.faceCheck.onDetectorUnavailable === 'refuse' || e.faceCheck.onDetectorUnavailable === 'refuse' ? 'refuse' : 'accept_for_review',
        },
    }),
};
export const riderPhotoSetting = (input) => marketRuleSetting(RIDER_PHOTO_SPEC, input);
export const resolveRiderPhoto = (settings, marketId, at, regionCode) => resolveMarketRule(RIDER_PHOTO_SPEC, settings, marketId, at, regionCode);
// ---- privacy.riderPhotoToDriver ---------------------------------------------------------------------------------------------
export const RIDER_PHOTO_TO_DRIVER_KEY = 'privacy.riderPhotoToDriver';
/** The moments, in ride order, the assigned driver may see the rider's photo: from the assignment until the rider boards. */
export const RIDER_PHOTO_DRIVER_PHASES = ['assigned', 'en_route', 'arrived', 'waiting', 'pin_pending'];
export const RIDER_PHOTO_TO_DRIVER_BOUNDS = { afterTripSeconds: { min: 0, max: 3_600 } };
export const DEFAULT_RIDER_PHOTO_TO_DRIVER = { visiblePhases: [...RIDER_PHOTO_DRIVER_PHASES], afterTripSeconds: 0 };
export const STRICT_RIDER_PHOTO_TO_DRIVER = { visiblePhases: [], afterTripSeconds: 0 };
export const RIDER_PHOTO_TO_DRIVER_REASON_CODES = ['RIDER_PHOTO_TO_DRIVER_GOVERNED', 'RIDER_PHOTO_TO_DRIVER_DEFAULT', 'RIDER_PHOTO_TO_DRIVER_INVALID'];
const inOrder = (phases) => RIDER_PHOTO_DRIVER_PHASES.filter((p) => phases.includes(p));
export function riderPhotoToDriverIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    if (!hasExactly(v, ['visiblePhases', 'afterTripSeconds']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const phases = v.visiblePhases;
    if (!Array.isArray(phases) || phases.some((p) => !RIDER_PHOTO_DRIVER_PHASES.includes(p)) || new Set(phases).size !== phases.length)
        return 'INVALID_VISIBLE_PHASES';
    if (!intWithin(v.afterTripSeconds, RIDER_PHOTO_TO_DRIVER_BOUNDS.afterTripSeconds.min, RIDER_PHOTO_TO_DRIVER_BOUNDS.afterTripSeconds.max))
        return 'INVALID_AFTER_TRIP_SECONDS';
    return undefined;
}
export const RIDER_PHOTO_TO_DRIVER_SPEC = {
    key: RIDER_PHOTO_TO_DRIVER_KEY,
    issue: riderPhotoToDriverIssue,
    copy: (v) => ({ visiblePhases: inOrder(v.visiblePhases), afterTripSeconds: v.afterTripSeconds }),
    defaultValue: DEFAULT_RIDER_PHOTO_TO_DRIVER,
    strictValue: STRICT_RIDER_PHOTO_TO_DRIVER,
    reasonPrefix: 'RIDER_PHOTO_TO_DRIVER',
    constraintClass: 'privacy',
    // Never shown in more moments, or for longer, than a layer above allows.
    tighten: (d, e) => ({ visiblePhases: inOrder(d.visiblePhases.filter((p) => e.visiblePhases.includes(p))), afterTripSeconds: Math.min(d.afterTripSeconds, e.afterTripSeconds) }),
};
export const riderPhotoToDriverSetting = (input) => marketRuleSetting(RIDER_PHOTO_TO_DRIVER_SPEC, input);
export const resolveRiderPhotoToDriver = (settings, marketId, at, regionCode) => resolveMarketRule(RIDER_PHOTO_TO_DRIVER_SPEC, settings, marketId, at, regionCode);
/** The D725 keys, in registry order (the e2e governed-settings journey leaves them unseeded by design). */
export const D725_RIDER_PHOTO_KEYS = [RIDER_PHOTO_KEY, RIDER_PHOTO_TO_DRIVER_KEY];
//# sourceMappingURL=rider-photo-policy.js.map